import { useEffect, useMemo } from "react";
import { Autocomplete, TextField } from "@mui/material";

import jrwaService, { IJrwaClassification, jrwaLabel } from "api/jrwaService";
import workspaceService from "api/workspaceService";
import { IWorkspaceStructure } from "dto/IWorkspace";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";

import styles from "./styles.module.scss";

const NO_DEPARTMENT_SYMBOL = "BRAK";

/** Symbol komórki albo — gdy pusty — najbliższej nadrzędnej z symbolem (jak BE GetUserDepartmentSymbol). */
const resolveUnitSymbol = (
  units: IWorkspaceStructure[],
  unitId?: string | null,
): string => {
  let cursor = unitId ?? null;
  for (let guard = 0; cursor && guard < 32; guard++) {
    const unit = units.find((u) => u.Id === cursor);
    if (!unit) break;
    if (unit.Symbol?.trim()) return unit.Symbol.trim();
    cursor = unit.ParentId ?? null;
  }
  return "";
};

// Wybór kodu JRWA i komórki organizacyjnej dla NOWEJ teczki + podgląd sygnatury
// SYMBOL.KOD.NR.ROK (port metryki teczki z Magistratu: klasyfikacja + SelectedUserDepartment).
// Numer nadaje BE przy zapisie (ciągły w ramach firmy, roku i kodu) — podgląd pokazuje
// numer, jaki teczka dostałaby w tej chwili.

export interface IJrwaSignatureValue {
  classificationId: string | null;
  departmentSymbol: string;
}

const JrwaSignaturePicker = ({
  value,
  onChange,
  disabled,
}: {
  value: IJrwaSignatureValue;
  onChange: (v: IJrwaSignatureValue) => void;
  disabled?: boolean;
}) => {
  const { data, isLoading } = jrwaService.useGetList();
  const { data: workspaceData } = workspaceService.useGetWorkspace(
    getWorkspaceId() ?? "",
  );

  const items = useMemo(
    () => (data?.data?.Items ?? []).filter((c) => !c.IsArchived),
    [data],
  );

  // Komórki organizacyjne firmy z BE (WorkspaceStructureItems) — symbol trafia do sygnatury.
  const structure = useMemo(
    () => workspaceData?.data?.Structure ?? [],
    [workspaceData],
  );
  const units = useMemo(
    () =>
      structure
        .map((u) => ({ id: u.Id, name: u.Name, symbol: resolveUnitSymbol(structure, u.Id) }))
        .filter((u) => !!u.symbol),
    [structure],
  );

  // Domyślna komórka = komórka zalogowanego w strukturze firmy (WorkspaceMembers.StructureId),
  // port Magistratu CurrentUserDepartmentSymbol; „BRAK", gdy nieprzypisany.
  useEffect(() => {
    if (value.departmentSymbol || !workspaceData?.data) return;
    const me = workspaceData.data.Members?.find((m) => m.IsMe);
    onChange({
      ...value,
      departmentSymbol:
        resolveUnitSymbol(structure, me?.StructureId) || NO_DEPARTMENT_SYMBOL,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tylko po dociągnięciu firmy
  }, [workspaceData]);

  const { data: preview } = jrwaService.useSignaturePreview(
    value.classificationId,
    value.departmentSymbol || NO_DEPARTMENT_SYMBOL,
  );

  const selected =
    items.find((c) => c.Id === value.classificationId) ?? null;

  // Symbol z profilu może nie występować na liście jednostek (np. schemat bez skrótów) —
  // dokładamy go, żeby Select nie pokazał pustej wartości.
  const symbolOptions = useMemo(() => {
    const set = new Map<string, string>();
    for (const u of units) set.set(u.symbol, `${u.symbol} — ${u.name}`);
    if (value.departmentSymbol && !set.has(value.departmentSymbol)) {
      set.set(value.departmentSymbol, value.departmentSymbol);
    }
    if (!set.has(NO_DEPARTMENT_SYMBOL)) {
      set.set(NO_DEPARTMENT_SYMBOL, `${NO_DEPARTMENT_SYMBOL} — bez komórki`);
    }
    return [...set.entries()];
  }, [units, value.departmentSymbol]);

  return (
    <div className={styles.wrapper}>
      <Autocomplete<IJrwaClassification>
        options={items}
        value={selected}
        loading={isLoading}
        disabled={disabled}
        getOptionLabel={(o) => jrwaLabel(o)}
        isOptionEqualToValue={(o, v) => o.Id === v.Id}
        filterOptions={(opts, state) => {
          const q = state.inputValue.trim().toLowerCase();
          if (!q) return opts.slice(0, 200);
          return opts
            .filter(
              (o) =>
                o.Code.toLowerCase().startsWith(q) ||
                o.Description.toLowerCase().includes(q),
            )
            .slice(0, 200);
        }}
        onChange={(_, v) => onChange({ ...value, classificationId: v?.Id ?? null })}
        renderOption={(props, o) => (
          <li {...props} key={o.Id}>
            <span className={styles.optCode}>{o.Code}</span>
            <span className={styles.optLabel}>{o.Description}</span>
            {/* Kod prowadzony wspólnie — teczka będzie widoczna dla wszystkich prowadzących kodu. */}
            {o.IsShared && (
              <span className={styles.optShared} title="Pracownicy prowadzą teczki wspólnie">
                wspólnie
              </span>
            )}
          </li>
        )}
        renderInput={(params) => (
          // Wariant/rozmiar jawnie: filled + small = 48 px (wzorzec wysokości pól w tym modalu).
          <TextField
            {...params}
            label="Kod klasyfikacyjny JRWA *"
            variant="filled"
            size="small"
            placeholder="symbol lub hasło…"
          />
        )}
        noOptionsText="Brak pozycji w wykazie"
      />

      <div className={styles.row}>
        {/* Ten sam Autocomplete co dla kodu JRWA — identyczna geometria pól w wierszu. */}
        <Autocomplete<[string, string], false, true, false>
          options={symbolOptions}
          value={
            symbolOptions.find(
              ([symbol]) => symbol === (value.departmentSymbol || NO_DEPARTMENT_SYMBOL),
            ) ?? symbolOptions[symbolOptions.length - 1]
          }
          disabled={disabled}
          disableClearable
          getOptionLabel={([, label]) => label}
          isOptionEqualToValue={(a, b) => a[0] === b[0]}
          onChange={(_, v) =>
            v && onChange({ ...value, departmentSymbol: v[0] })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Komórka organizacyjna"
              variant="filled"
              size="small"
            />
          )}
        />

        <div
          className={styles.preview}
          title="Numer teczki nadawany jest przy zapisie (ciągły w ramach roku i kodu)"
        >
          <div className={styles.previewLabel}>Sygnatura</div>
          <div className={styles.previewValue}>
            {value.classificationId && preview?.data?.Signature
              ? preview.data.Signature
              : "—"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JrwaSignaturePicker;
